import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Loader2, Mail, Send, CheckCircle2, Plus, X, Users, AlertCircle, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface InviteUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const InviteUserDialog = ({ open, onOpenChange }: InviteUserDialogProps) => {
    const { t } = useTranslation();
    const [emails, setEmails] = useState<string[]>([""]);
    const [role, setRole] = useState<"user" | "admin">("user");
    const [position, setPosition] = useState("");
    const [department, setDepartment] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [results, setResults] = useState<
        { email: string; success: boolean; error?: string; invitationLink?: string; method?: "email" | "copy" }[]
    >([]);
    const [showResults, setShowResults] = useState(false);
    const [copiedLinks, setCopiedLinks] = useState<Set<string>>(new Set());

    const utils = trpc.useUtils();

    const createInvitationMutation = trpc.invitations.create.useMutation();

    // Parse emails from pasted text (supports comma, semicolon, newline, space separated)
    const parseEmails = useCallback((text: string): string[] => {
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const matches = text.match(emailRegex) || [];
        return [...new Set(matches)]; // Remove duplicates
    }, []);

    const handleEmailChange = (index: number, value: string) => {
        // Check if pasted content contains multiple emails
        const parsedEmails = parseEmails(value);

        if (parsedEmails.length > 1) {
            // Multiple emails pasted - add them all
            const newEmails = [...emails];
            newEmails.splice(index, 1, ...parsedEmails);
            // Remove empty strings and duplicates
            const uniqueEmails = [...new Set(newEmails.filter((e) => e.trim()))];
            if (uniqueEmails.length === 0) uniqueEmails.push("");
            setEmails(uniqueEmails);
        } else {
            // Single email or partial input
            const newEmails = [...emails];
            newEmails[index] = value;
            setEmails(newEmails);
        }
    };

    const addEmailField = () => {
        setEmails([...emails, ""]);
    };

    const removeEmailField = (index: number) => {
        if (emails.length > 1) {
            const newEmails = emails.filter((_, i) => i !== index);
            setEmails(newEmails);
        }
    };

    const handleClose = () => {
        setEmails([""]);
        setRole("user");
        setPosition("");
        setDepartment("");
        setResults([]);
        setShowResults(false);
        setCopiedLinks(new Set());
        onOpenChange(false);
    };

    const handleSendMore = () => {
        setEmails([""]);
        setResults([]);
        setShowResults(false);
        setCopiedLinks(new Set());
    };

    const handleCopyLinks = async () => {
        const validEmails = emails.filter((e) => e.trim() && e.includes("@"));

        if (validEmails.length === 0) {
            toast.error(t("invitations.form.noValidEmails"));
            return;
        }

        setIsCopying(true);
        const inviteResults: {
            email: string;
            success: boolean;
            error?: string;
            invitationLink?: string;
            method?: "email" | "copy";
        }[] = [];

        const isSingleInvite = validEmails.length === 1;

        for (const email of validEmails) {
            try {
                const result = await createInvitationMutation.mutateAsync({
                    email: email.trim(),
                    role,
                    position: isSingleInvite ? position || undefined : undefined,
                    department: isSingleInvite ? department || undefined : undefined,
                });

                // Copy link to clipboard
                try {
                    await navigator.clipboard.writeText(result.invitationLink);
                    inviteResults.push({
                        email,
                        success: true,
                        invitationLink: result.invitationLink,
                        method: "copy",
                    });
                } catch (clipboardError) {
                    inviteResults.push({
                        email,
                        success: false,
                        error: t("invitations.messages.copyFailed"),
                        method: "copy",
                    });
                }
            } catch (error: any) {
                inviteResults.push({
                    email,
                    success: false,
                    error: error.message || t("invitations.messages.invitationFailed"),
                    method: "copy",
                });
            }
        }

        setIsCopying(false);
        setResults(inviteResults);
        setShowResults(true);

        // Invalidate queries
        utils.invitations.findMany.invalidate();
        utils.invitations.findPending.invalidate();

        const successCount = inviteResults.filter((r) => r.success).length;
        if (successCount > 0) {
            if (successCount === 1) {
                toast.success(t("invitations.messages.linkCopied"));
            } else {
                toast.success(t("invitations.messages.linksCopied", { count: successCount }));
            }
        }
    };

    const handleSendEmails = async () => {
        const validEmails = emails.filter((e) => e.trim() && e.includes("@"));

        if (validEmails.length === 0) {
            toast.error(t("invitations.form.noValidEmails"));
            return;
        }

        setIsSending(true);
        const inviteResults: {
            email: string;
            success: boolean;
            error?: string;
            invitationLink?: string;
            method?: "email" | "copy";
        }[] = [];

        const isSingleInvite = validEmails.length === 1;

        for (const email of validEmails) {
            try {
                const result = await createInvitationMutation.mutateAsync({
                    email: email.trim(),
                    role,
                    position: isSingleInvite ? position || undefined : undefined,
                    department: isSingleInvite ? department || undefined : undefined,
                });
                inviteResults.push({
                    email,
                    success: true,
                    invitationLink: result.invitationLink,
                    method: "email",
                });
            } catch (error: any) {
                inviteResults.push({
                    email,
                    success: false,
                    error: error.message || t("invitations.messages.invitationFailed"),
                    method: "email",
                });
            }
        }

        setIsSending(false);
        setResults(inviteResults);
        setShowResults(true);

        // Invalidate queries
        utils.invitations.findMany.invalidate();
        utils.invitations.findPending.invalidate();

        const successCount = inviteResults.filter((r) => r.success).length;
        if (successCount > 0) {
            toast.success(t("invitations.mass.successCount", { count: successCount }));
        }
    };

    const validEmailCount = emails.filter((e) => e.trim() && e.includes("@")).length;

    // Results view
    if (showResults) {
        const successResults = results.filter((r) => r.success);
        const failedResults = results.filter((r) => !r.success);
        const allSuccess = failedResults.length === 0;

        // Limit display to first 5 items
        const MAX_DISPLAY = 5;
        const displayedSuccess = successResults.slice(0, MAX_DISPLAY);
        const remainingSuccess = successResults.length - MAX_DISPLAY;
        const displayedFailed = failedResults.slice(0, MAX_DISPLAY);
        const remainingFailed = failedResults.length - MAX_DISPLAY;

        return (
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="max-w-lg">
                    <div className="flex flex-col space-y-6 py-2">
                        {/* Header with Icon */}
                        <div className="flex flex-col items-center space-y-3 text-center">
                            <div
                                className={cn(
                                    "flex h-16 w-16 items-center justify-center rounded-full",
                                    allSuccess ? "bg-green-500/10" : "bg-amber-500/10"
                                )}
                            >
                                {allSuccess ? (
                                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                                ) : (
                                    <AlertCircle className="h-8 w-8 text-amber-600" />
                                )}
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-xl font-semibold">{t("invitations.mass.resultsTitle")}</h3>
                                <p className="text-sm text-muted-foreground">
                                    {t("invitations.mass.resultsDescription", {
                                        success: successResults.length,
                                        total: results.length,
                                    })}
                                </p>
                            </div>
                        </div>

                        <Separator />

                        {/* Results Cards */}
                        <div className="max-h-[400px] space-y-4 overflow-y-auto px-1">
                            {/* Success Results */}
                            {successResults.length > 0 && (
                                <Card className="border-green-200 bg-green-50/50 p-4 dark:border-green-900 dark:bg-green-950/20">
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                                            <h4 className="text-sm font-semibold text-green-700 dark:text-green-400">
                                                {successResults[0]?.method === "copy"
                                                    ? t("invitations.mass.copied")
                                                    : t("invitations.mass.sent")}{" "}
                                                ({successResults.length})
                                            </h4>
                                        </div>
                                        <div className="space-y-2">
                                            {displayedSuccess.map((r, i) => {
                                                const isCopied = copiedLinks.has(r.email);
                                                return (
                                                    <div
                                                        key={i}
                                                        className="flex items-center gap-2 rounded-md bg-white/50 p-2 dark:bg-gray-900/50"
                                                    >
                                                        <Badge
                                                            variant="secondary"
                                                            className="flex-1 border-green-200 bg-green-100 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
                                                        >
                                                            <Mail className="mr-1 h-3 w-3" />
                                                            {r.email}
                                                        </Badge>
                                                        {r.invitationLink && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 shrink-0 px-2"
                                                                onClick={async () => {
                                                                    try {
                                                                        await navigator.clipboard.writeText(
                                                                            r.invitationLink!
                                                                        );
                                                                        setCopiedLinks((prev) =>
                                                                            new Set(prev).add(r.email)
                                                                        );
                                                                        toast.success(
                                                                            t("invitations.messages.linkCopied")
                                                                        );
                                                                        setTimeout(() => {
                                                                            setCopiedLinks((prev) => {
                                                                                const newSet = new Set(prev);
                                                                                newSet.delete(r.email);
                                                                                return newSet;
                                                                            });
                                                                        }, 2000);
                                                                    } catch (error) {
                                                                        toast.error(
                                                                            t("invitations.messages.copyFailed")
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {isCopied ? (
                                                                    <Check className="h-3.5 w-3.5 text-green-600" />
                                                                ) : (
                                                                    <Copy className="h-3.5 w-3.5" />
                                                                )}
                                                            </Button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {remainingSuccess > 0 && (
                                                <div className="rounded-md bg-white/50 p-2 dark:bg-gray-900/50">
                                                    <Badge
                                                        variant="secondary"
                                                        className="border-green-200 bg-green-100 font-medium text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                                                    >
                                                        ... and {remainingSuccess} more
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {/* Failed Results */}
                            {failedResults.length > 0 && (
                                <Card className="border-red-200 bg-red-50/50 p-4 dark:border-red-900 dark:bg-red-950/20">
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 text-red-600" />
                                            <h4 className="text-sm font-semibold text-red-700 dark:text-red-400">
                                                {t("invitations.mass.failed")} ({failedResults.length})
                                            </h4>
                                        </div>
                                        <div className="space-y-2">
                                            {displayedFailed.map((r, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-start gap-2 rounded-md bg-white/50 p-2 dark:bg-gray-900/50"
                                                >
                                                    <Badge
                                                        variant="secondary"
                                                        className="shrink-0 border-red-200 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
                                                    >
                                                        {r.email}
                                                    </Badge>
                                                    <span className="flex-1 text-xs text-red-600 dark:text-red-400">
                                                        {r.error}
                                                    </span>
                                                </div>
                                            ))}
                                            {remainingFailed > 0 && (
                                                <div className="rounded-md bg-white/50 p-2 dark:bg-gray-900/50">
                                                    <Badge
                                                        variant="secondary"
                                                        className="border-red-200 bg-red-100 font-medium text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400"
                                                    >
                                                        ... and {remainingFailed} more failed
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={handleClose} className="flex-1">
                                {t("common.close")}
                            </Button>
                            <Button onClick={handleSendMore} className="flex-1">
                                <Users className="mr-2 h-4 w-4" />
                                {t("invitations.mass.inviteMore")}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        {t("invitations.dialog.title")}
                    </DialogTitle>
                    <DialogDescription>{t("invitations.dialog.descriptionMass")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 overflow-visible">
                    {/* Email fields */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>{t("invitations.form.emails")}</Label>
                            <span className="text-xs text-muted-foreground">
                                {validEmailCount} {t("invitations.form.emailsCount")}
                            </span>
                        </div>
                        <div className={`-m-1 space-y-2 p-1 ${emails.length > 4 ? "max-h-40 overflow-y-auto" : ""}`}>
                            {emails.map((email, index) => (
                                <div key={index} className="flex gap-2">
                                    <Input
                                        type="email"
                                        placeholder="colleague@company.com"
                                        value={email}
                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                        onPaste={(e) => {
                                            const pastedText = e.clipboardData.getData("text");
                                            const parsedEmails = parseEmails(pastedText);
                                            if (parsedEmails.length > 1) {
                                                e.preventDefault();
                                                handleEmailChange(index, pastedText);
                                            }
                                        }}
                                        autoComplete="off"
                                        data-1p-ignore
                                        data-lpignore="true"
                                        data-form-type="other"
                                    />
                                    {emails.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="shrink-0"
                                            onClick={() => removeEmailField(index)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={addEmailField}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t("invitations.form.addEmail")}
                        </Button>
                        <p className="text-xs text-muted-foreground">{t("invitations.form.pasteHint")}</p>
                    </div>

                    {/* Role selection */}
                    <div className="space-y-2">
                        <Label>{t("invitations.form.role")}</Label>
                        <Select value={role} onValueChange={(v) => setRole(v as "user" | "admin")}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("invitations.form.selectRole")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="user">{t("columns.user")}</SelectItem>
                                <SelectItem value="admin">{t("columns.admin")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Position & Department - only for single invites */}
                    {validEmailCount <= 1 && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t("invitations.form.position")}</Label>
                                <Input
                                    placeholder="Developer"
                                    value={position}
                                    onChange={(e) => setPosition(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t("invitations.form.department")}</Label>
                                <Input
                                    placeholder="Engineering"
                                    value={department}
                                    onChange={(e) => setDepartment(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={handleClose}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleCopyLinks}
                            disabled={isCopying || isSending || validEmailCount === 0}
                        >
                            {isCopying ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("invitations.form.copying")}
                                </>
                            ) : (
                                <>
                                    <Copy className="mr-2 h-4 w-4" />
                                    {validEmailCount > 1
                                        ? t("invitations.form.copyLinks", { count: validEmailCount })
                                        : t("invitations.form.copyLink")}
                                </>
                            )}
                        </Button>
                        <Button onClick={handleSendEmails} disabled={isSending || isCopying || validEmailCount === 0}>
                            {isSending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("invitations.form.sending")}
                                </>
                            ) : (
                                <>
                                    <Send className="mr-2 h-4 w-4" />
                                    {validEmailCount > 1
                                        ? t("invitations.form.sendInvites", { count: validEmailCount })
                                        : t("invitations.form.sendInvite")}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default InviteUserDialog;
